import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from '@/components/charts/recharts';
import { TrendingUp, Wallet, PiggyBank, ArrowRight, Flame, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';

function formatIndianNumber(num) {
  if (num === null || num === undefined || Number.isNaN(num)) return '';
  const numStr = Math.round(num).toString();
  let lastThree = numStr.substring(numStr.length - 3);
  let otherNumbers = numStr.substring(0, numStr.length - 3);
  if (otherNumbers !== '') lastThree = `,${lastThree}`;
  return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + lastThree;
}

function parseNumber(str) {
  if (str === null || str === undefined) return 0;
  const cleaned = String(str).replace(/,/g, '').trim();
  if (cleaned === '') return NaN;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : NaN;
}

export default function FIRECalculator() {
  const [monthlyExpenses, setMonthlyExpenses] = useState(50000);
  const [inflationRate, setInflationRate] = useState(6);
  const [expectedReturn, setExpectedReturn] = useState(10);
  const [safeWithdrawalRate, setSafeWithdrawalRate] = useState(4);
  const [currentSavings, setCurrentSavings] = useState(1000000);
  const [monthlyInvestment, setMonthlyInvestment] = useState(30000);
  const [enableStepUp, setEnableStepUp] = useState(false);
  const [stepUpType, setStepUpType] = useState('percentage');
  const [stepUpValue, setStepUpValue] = useState(10);

  // Input display states (support clear + Indian formatting for amounts)
  const [monthlyExpensesInput, setMonthlyExpensesInput] = useState(formatIndianNumber(50000));
  const [inflationRateInput, setInflationRateInput] = useState('6');
  const [expectedReturnInput, setExpectedReturnInput] = useState('10');
  const [safeWithdrawalRateInput, setSafeWithdrawalRateInput] = useState('4');
  const [currentSavingsInput, setCurrentSavingsInput] = useState(formatIndianNumber(1000000));
  const [monthlyInvestmentInput, setMonthlyInvestmentInput] = useState(formatIndianNumber(30000));
  const [stepUpValueInput, setStepUpValueInput] = useState('10');

  const results = useMemo(() => {
    // FIRE Number = Annual Expenses / Safe Withdrawal Rate
    const annualExpenses = monthlyExpenses * 12;
    const fireNumber = annualExpenses / (safeWithdrawalRate / 100);
    
    // Calculate years to achieve FIRE
    const monthlyRate = expectedReturn / 100 / 12;
    const realReturnRate = (expectedReturn - inflationRate) / 100 / 12;
    
    let corpus = currentSavings;
    let totalSIPInvestment = 0;
    let currentMonthlyInvestment = monthlyInvestment;
    let months = 0;
    const maxMonths = 600; // 50 years max
    const yearlyData = [];
    
    // Adjust FIRE number for inflation each year
    let inflationAdjustedFireNumber = fireNumber;
    
    while (corpus < inflationAdjustedFireNumber && months < maxMonths) {
      totalSIPInvestment += currentMonthlyInvestment;
      corpus = (corpus + currentMonthlyInvestment) * (1 + monthlyRate);
      months++;
      
      // Record yearly data and adjust FIRE target for inflation
      if (months % 12 === 0) {
        const year = months / 12;
        inflationAdjustedFireNumber = fireNumber * Math.pow(1 + inflationRate / 100, year);
        yearlyData.push({
          year: `Year ${year}`,
          yearNum: year,
          corpus: Math.round(corpus),
          fireTarget: Math.round(inflationAdjustedFireNumber),
          investment: Math.round(currentSavings + totalSIPInvestment),
        });

        if (enableStepUp) {
          currentMonthlyInvestment = stepUpType === 'percentage'
            ? currentMonthlyInvestment * (1 + stepUpValue / 100)
            : currentMonthlyInvestment + stepUpValue;
          currentMonthlyInvestment = Math.max(0, currentMonthlyInvestment);
        }
      }
    }
    
    const yearsToFIRE = months / 12;
    const monthsRemaining = months % 12;
    
    // Calculate total investment
    const totalInvestment = currentSavings + totalSIPInvestment;

    return {
      fireNumber: Math.round(fireNumber),
      yearsToFIRE: Math.floor(yearsToFIRE),
      monthsToFIRE: Math.round(monthsRemaining),
      totalYears: yearsToFIRE,
      finalCorpus: Math.round(corpus),
      totalInvestment: Math.round(totalInvestment),
      totalReturns: Math.round(corpus - totalInvestment),
      yearlyData: yearlyData.slice(0, Math.min(50, yearlyData.length)),
      achievable: months < maxMonths,
    };
  }, [monthlyExpenses, inflationRate, expectedReturn, safeWithdrawalRate, currentSavings, monthlyInvestment, enableStepUp, stepUpType, stepUpValue]);

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    return `₹${value.toLocaleString('en-IN')}`;
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-text-primary mb-4">
            <Flame className="inline-block h-12 w-12 text-orange-500 mr-3" />
            FIRE Calculator
          </h1>
          <p className="text-text-secondary text-lg max-w-2xl mx-auto">
            Calculate your Financial Independence, Retire Early (FIRE) number and see how long it takes to achieve it
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="glass-effect rounded-2xl p-8">
            <h2 className="font-heading text-2xl font-bold text-text-primary mb-6">Your FIRE Details</h2>
            
            <div className="space-y-5">
              <div>
                <label className="block text-text-secondary text-sm mb-2">Monthly Expenses (₹)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={monthlyExpensesInput}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setMonthlyExpensesInput(raw);
                    if (raw.trim() === '') return;
                    const n = parseInt(raw.replace(/,/g, ''), 10);
                    if (!Number.isFinite(n)) return;
                    setMonthlyExpenses(Math.max(10000, n));
                  }}
                  onFocus={() => setMonthlyExpensesInput(String(monthlyExpenses))}
                  onBlur={() => {
                    const n = parseInt(monthlyExpensesInput.replace(/,/g, '').trim(), 10);
                    const next = Number.isFinite(n) ? Math.max(10000, n) : monthlyExpenses;
                    setMonthlyExpenses(next);
                    setMonthlyExpensesInput(formatIndianNumber(next));
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  data-testid="fire-monthly-expenses"
                />
                <input
                  type="range"
                  min="10000"
                  max="500000"
                  step="5000"
                  value={monthlyExpenses}
                  onChange={(e) => {
                    const next = parseInt(e.target.value);
                    setMonthlyExpenses(next);
                    setMonthlyExpensesInput(formatIndianNumber(next));
                  }}
                  className="w-full mt-2 accent-primary"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Inflation Rate (%)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={inflationRateInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setInflationRateInput(raw);
                      if (raw.trim() === '') return;
                      const n = parseNumber(raw);
                      if (!Number.isFinite(n)) return;
                      if (n >= 2 && n <= 15) setInflationRate(n);
                    }}
                    onBlur={() => {
                      if (inflationRateInput.trim() === '') {
                        setInflationRateInput(String(inflationRate));
                        return;
                      }
                      const n = parseNumber(inflationRateInput);
                      const next = Number.isFinite(n) ? Math.max(2, Math.min(15, n)) : inflationRate;
                      setInflationRate(next);
                      setInflationRateInput(String(next));
                    }}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                    step="0.5"
                    data-testid="fire-inflation"
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Expected Return (%) - 2-12%</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={expectedReturnInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setExpectedReturnInput(raw);
                      if (raw.trim() === '') return;
                      const n = parseNumber(raw);
                      if (!Number.isFinite(n)) return;
                      if (n >= 2 && n <= 12) setExpectedReturn(n);
                    }}
                    onBlur={() => {
                      if (expectedReturnInput.trim() === '') {
                        setExpectedReturnInput(String(expectedReturn));
                        return;
                      }
                      const n = parseNumber(expectedReturnInput);
                      const next = Number.isFinite(n) ? Math.max(2, Math.min(12, n)) : expectedReturn;
                      setExpectedReturn(next);
                      setExpectedReturnInput(String(next));
                    }}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                    min="2"
                    max="12"
                    step="0.5"
                    data-testid="fire-expected-return"
                  />
                </div>
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-2">Safe Withdrawal Rate (%) - Default 4%</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={safeWithdrawalRateInput}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setSafeWithdrawalRateInput(raw);
                    if (raw.trim() === '') return;
                    const n = parseNumber(raw);
                    if (!Number.isFinite(n)) return;
                    if (n >= 2 && n <= 6) setSafeWithdrawalRate(n);
                  }}
                  onBlur={() => {
                    if (safeWithdrawalRateInput.trim() === '') {
                      setSafeWithdrawalRateInput(String(safeWithdrawalRate));
                      return;
                    }
                    const n = parseNumber(safeWithdrawalRateInput);
                    const next = Number.isFinite(n) ? Math.max(2, Math.min(6, n)) : safeWithdrawalRate;
                    setSafeWithdrawalRate(next);
                    setSafeWithdrawalRateInput(String(next));
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  min="2"
                  max="6"
                  step="0.5"
                  data-testid="fire-withdrawal-rate"
                />
                <p className="text-text-muted text-xs mt-1">The % of corpus you'll withdraw annually post-FIRE</p>
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-2">Current Savings (₹)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={currentSavingsInput}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setCurrentSavingsInput(raw);
                    if (raw.trim() === '') return;
                    const n = parseInt(raw.replace(/,/g, ''), 10);
                    if (!Number.isFinite(n)) return;
                    setCurrentSavings(Math.max(0, n));
                  }}
                  onFocus={() => setCurrentSavingsInput(String(currentSavings))}
                  onBlur={() => {
                    const n = parseInt(currentSavingsInput.replace(/,/g, '').trim(), 10);
                    const next = Number.isFinite(n) ? Math.max(0, n) : currentSavings;
                    setCurrentSavings(next);
                    setCurrentSavingsInput(formatIndianNumber(next));
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  data-testid="fire-current-savings"
                />
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-2">Monthly Investment (₹)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={monthlyInvestmentInput}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setMonthlyInvestmentInput(raw);
                    if (raw.trim() === '') return;
                    const n = parseInt(raw.replace(/,/g, ''), 10);
                    if (!Number.isFinite(n)) return;
                    setMonthlyInvestment(Math.max(1000, n));
                  }}
                  onFocus={() => setMonthlyInvestmentInput(String(monthlyInvestment))}
                  onBlur={() => {
                    const n = parseInt(monthlyInvestmentInput.replace(/,/g, '').trim(), 10);
                    const next = Number.isFinite(n) ? Math.max(1000, n) : monthlyInvestment;
                    setMonthlyInvestment(next);
                    setMonthlyInvestmentInput(formatIndianNumber(next));
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  data-testid="fire-monthly-investment"
                />
                <input
                  type="range"
                  min="1000"
                  max="200000"
                  step="1000"
                  value={monthlyInvestment}
                  onChange={(e) => {
                    const next = parseInt(e.target.value);
                    setMonthlyInvestment(next);
                    setMonthlyInvestmentInput(formatIndianNumber(next));
                  }}
                  className="w-full mt-2 accent-primary"
                />
              </div>

              <div className="pt-4 border-t border-white/10">
                <label className="flex items-center gap-3 text-text-primary select-none">
                  <input
                    type="checkbox"
                    checked={enableStepUp}
                    onChange={(e) => setEnableStepUp(e.target.checked)}
                    className="h-5 w-5 accent-primary"
                  />
                  <span className="text-sm font-medium">Enable Step-Up SIP</span>
                </label>

                {enableStepUp && (
                  <div className="mt-3 space-y-3">
                    <div className="flex items-center gap-6 text-sm text-text-secondary">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="radio"
                          name="fire-stepup-type"
                          checked={stepUpType === 'percentage'}
                          onChange={() => {
                            setStepUpType('percentage');
                            setStepUpValueInput(String(stepUpValue));
                          }}
                          className="accent-primary"
                        />
                        <span>% annually</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="radio"
                          name="fire-stepup-type"
                          checked={stepUpType === 'fixed'}
                          onChange={() => {
                            setStepUpType('fixed');
                            setStepUpValueInput(formatIndianNumber(stepUpValue));
                          }}
                          className="accent-primary"
                        />
                        <span>Fixed amount</span>
                      </label>
                    </div>

                    <div>
                      <label className="block text-text-secondary text-sm mb-2">
                        {stepUpType === 'percentage' ? 'Annual Step-Up (%)' : 'Annual Step-Up Amount (₹)'}
                      </label>
                      <input
                        type="text"
                        inputMode={stepUpType === 'percentage' ? 'decimal' : 'numeric'}
                        value={stepUpValueInput}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setStepUpValueInput(raw);
                          if (raw.trim() === '') return;
                          const n = parseNumber(raw);
                          if (!Number.isFinite(n)) return;
                          setStepUpValue(Math.max(0, n));
                        }}
                        onFocus={() => setStepUpValueInput(String(stepUpValue))}
                        onBlur={() => {
                          if (stepUpValueInput.trim() === '') {
                            setStepUpValueInput(stepUpType === 'fixed' ? formatIndianNumber(stepUpValue) : String(stepUpValue));
                            return;
                          }
                          const n = parseNumber(stepUpValueInput);
                          const next = Number.isFinite(n) ? Math.max(0, n) : stepUpValue;
                          setStepUpValue(next);
                          setStepUpValueInput(stepUpType === 'fixed' ? formatIndianNumber(next) : String(next));
                        }}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                      />
                      <p className="text-text-muted text-xs mt-1">Applies once every 12 months.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-6">
            {/* FIRE Number Highlight */}
            <div className="glass-effect rounded-2xl p-6 text-center border-2 border-orange-500/30">
              <Flame className="h-12 w-12 text-orange-500 mx-auto mb-3" />
              <p className="text-text-muted text-sm mb-1">Your FIRE Number</p>
              <p className="text-4xl font-bold text-orange-500">{formatCurrency(results.fireNumber)}</p>
              <p className="text-text-secondary text-sm mt-2">The corpus needed to retire and live off investments</p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="glass-effect rounded-xl p-5 text-center">
                <Target className="h-7 w-7 text-primary mx-auto mb-2" />
                <p className="text-text-muted text-xs">Time to FIRE</p>
                <p className="text-xl font-bold text-text-primary">
                  {results.achievable ? `${results.yearsToFIRE}Y ${results.monthsToFIRE}M` : '50+ Years'}
                </p>
              </div>
              <div className="glass-effect rounded-xl p-5 text-center">
                <Wallet className="h-7 w-7 text-secondary mx-auto mb-2" />
                <p className="text-text-muted text-xs">Total Investment</p>
                <p className="text-xl font-bold text-secondary">{formatCurrency(results.totalInvestment)}</p>
              </div>
              <div className="glass-effect rounded-xl p-5 text-center">
                <TrendingUp className="h-7 w-7 text-green-400 mx-auto mb-2" />
                <p className="text-text-muted text-xs">Total Returns</p>
                <p className="text-xl font-bold text-green-400">{formatCurrency(results.totalReturns)}</p>
              </div>
              <div className="glass-effect rounded-xl p-5 text-center">
                <PiggyBank className="h-7 w-7 text-blue-400 mx-auto mb-2" />
                <p className="text-text-muted text-xs">Final Corpus</p>
                <p className="text-xl font-bold text-blue-400">{formatCurrency(results.finalCorpus)}</p>
              </div>
            </div>

            {/* Insight */}
            <div className="glass-effect rounded-xl p-4 border-l-4 border-orange-500">
              <p className="text-text-secondary">
                <span className="text-orange-500 font-semibold">Insight:</span> At your current pace, you achieve FIRE in <span className="text-primary font-bold">{results.yearsToFIRE} years {results.monthsToFIRE} months</span>. Your monthly expenses of {formatCurrency(monthlyExpenses)} require a corpus of {formatCurrency(results.fireNumber)} at a {safeWithdrawalRate}% withdrawal rate.
              </p>
            </div>

            {/* Chart */}
            <div className="glass-effect rounded-2xl p-6">
              <h3 className="font-heading text-xl font-bold text-text-primary mb-4">Wealth Accumulation vs FIRE Target</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={results.yearlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="year" stroke="#94a3b8" fontSize={11} />
                    <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `₹${(v/10000000).toFixed(1)}Cr`} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Line type="monotone" dataKey="fireTarget" name="FIRE Target" stroke="#f97316" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                    <Line type="monotone" dataKey="corpus" name="Your Wealth" stroke="#F5A623" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Year-wise Table */}
        <div className="mt-8 glass-effect rounded-2xl p-6">
          <h3 className="font-heading text-xl font-bold text-text-primary mb-4">Year-wise Progress</h3>
          <div className="w-full max-h-[70vh] overflow-auto [-webkit-overflow-scrolling:touch]">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="sticky top-0 bg-background/95 backdrop-blur z-20 whitespace-nowrap text-left py-3 px-4 text-text-muted font-medium">Year</th>
                  <th className="sticky top-0 bg-background/95 backdrop-blur z-20 whitespace-nowrap text-right py-3 px-4 text-text-muted font-medium">Investment</th>
                  <th className="sticky top-0 bg-background/95 backdrop-blur z-20 whitespace-nowrap text-right py-3 px-4 text-text-muted font-medium">Corpus</th>
                  <th className="sticky top-0 bg-background/95 backdrop-blur z-20 whitespace-nowrap text-right py-3 px-4 text-text-muted font-medium">FIRE Target</th>
                  <th className="sticky top-0 bg-background/95 backdrop-blur z-20 whitespace-nowrap text-right py-3 px-4 text-text-muted font-medium">Progress</th>
                </tr>
              </thead>
              <tbody>
                {results.yearlyData.slice(0, 20).map((row, index) => {
                  const progress = Math.min(100, (row.corpus / row.fireTarget) * 100);
                  return (
                    <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                      <td className="whitespace-nowrap py-3 px-4 text-text-primary">{row.year}</td>
                      <td className="whitespace-nowrap py-3 px-4 text-right text-text-secondary">{formatCurrency(row.investment)}</td>
                      <td className="whitespace-nowrap py-3 px-4 text-right text-primary font-medium">{formatCurrency(row.corpus)}</td>
                      <td className="whitespace-nowrap py-3 px-4 text-right text-orange-400">{formatCurrency(row.fireTarget)}</td>
                      <td className="whitespace-nowrap py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-20 h-2 bg-white/10 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${progress >= 100 ? 'bg-green-400' : 'bg-primary'}`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <span className={`text-sm ${progress >= 100 ? 'text-green-400' : 'text-text-muted'}`}>
                            {progress.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 glass-effect rounded-xl p-4 border border-yellow-500/30">
          <p className="text-text-muted text-sm">
            <span className="text-yellow-500 font-semibold">Disclaimer:</span> The FIRE calculations are based on the inputs provided and assume constant returns and inflation. The 4% Safe Withdrawal Rate is a general guideline and may not be suitable for all situations. Actual results may vary based on market conditions, sequence of returns risk, and personal circumstances. These projections are for illustrative purposes only. Please consult a qualified financial advisor before making investment decisions.
          </p>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <div className="glass-effect rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="font-heading text-2xl font-bold text-text-primary mb-4">Get a Personalized Investment Plan</h3>
            <p className="text-text-secondary mb-6">Let us help you create a customized FIRE strategy to achieve financial independence faster</p>
            <Link to="/contact">
              <Button className="btn-primary text-lg px-8 py-4">
                Get Started <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
